/**
 * The two commercial brands this codebase serves, and everything that differs
 * between them.
 *
 * In `packages/shared` and not beside any one caller, for the reason
 * `TEMPLATE_MEDIA_LIMITS` and `promotionPriceCents` are: all three apps read it.
 * The backend builds deep links, email senders and the privacy-notice URL from
 * it; the CRM renders the wordmark; the widget renders the fallback mark. A
 * second copy anywhere is a brand that drifts on one surface and nobody notices
 * until a real person receives it.
 *
 * WHAT DECIDES THE BRAND. The client does — `clients.brand`. The hostname gets
 * exactly one job, `brandByHostname`, and only for the screens that have no
 * client to ask: login, the auth callback, the public privacy notice. Everywhere
 * else the request may not exist at all (the digest scheduler, visit
 * notifications, Stripe webhooks, WhatsApp alerts), which is why the answer lives
 * on a row and not on a `Host` header.
 */
/** The registry keys, declared once so the Zod enum and the map cannot diverge. */
export const BRAND_KEYS = ["scholaops", "general"];
export const BRANDS = {
    scholaops: {
        key: "scholaops",
        name: "ScholaOps",
        tagline: "Asistente de admisiones con IA",
        wordmark: {
            kind: "text",
            lead: "schola",
            tail: "ops",
            suffix: ".ai",
            accent: { onLight: "text-lime-deep", onDark: "text-lime" },
        },
        // `/favicon.png` stays where it has always been: every digest already sitting
        // in an inbox points at it.
        mark: { shape: "diamond", emailPngPath: "/favicon.png" },
        appUrl: "https://app.scholaops.ai",
        siteUrl: "https://scholaops.ai",
        hostnames: ["app.scholaops.ai", "scholaops.ai", "www.scholaops.ai"],
        privacyNoticePath: "/aviso-de-privacidad",
        termsPath: "/terminos-y-condiciones",
        colors: { ink: "#0E0D0A", accent: "#D7F26B", accentDeep: "#617E00" },
        assignable: true,
    },
    /**
     * Loxago — the general/store brand, named 2026-09-28.
     *
     * `general` is an INTERNAL KEY, not a naming decision: the name lives here and
     * not in the `client_brand` enum, so naming the product was a string edit and
     * nothing in the database moved.
     *
     * Contrast, checked before the pair went in (§6.13): `accentDeep` #077941 is
     * 5.5:1 on white and `accent` #4DAE6A about 7:1 on #0B0C0A; the dark-ground
     * gradient clears 5.5:1 on ink-900 at its darker end. The LIGHT-ground gradient
     * ends at #69AE67, ~2.7:1 on white — acceptable because a logotype is exempt
     * from WCAG 1.4.3 and its accessible name rides on `aria-label`, not on pixels.
     * Do not "fix" it by darkening the stop; that is the brand's own artwork.
     */
    general: {
        key: "general",
        name: "Loxago",
        tagline: "Asistente comercial con IA",
        wordmark: {
            kind: "logotype",
            gradient: { onLight: ["#077941", "#69AE67"], onDark: ["#2E9A5C", "#8FCB86"] },
            emailPng: { path: "/brand/loxago/logotype-light.png", width: 121, height: 20 },
        },
        mark: { shape: "chevron", emailPngPath: "/brand/loxago/mark-180.png" },
        appUrl: "https://app.loxago.ai",
        siteUrl: "https://loxago.ai",
        hostnames: ["app.loxago.ai", "loxago.ai", "www.loxago.ai"],
        privacyNoticePath: "/aviso-de-privacidad",
        termsPath: "/terminos-y-condiciones",
        colors: { ink: "#111111", accent: "#4DAE6A", accentDeep: "#077941" },
        assignable: true,
    },
};
/**
 * The brand every "we could not tell" path falls back to: an unknown hostname, a
 * super-admin who has not picked a school yet, our own platform roster, a client
 * row that vanished between two reads.
 */
export const DEFAULT_BRAND = BRANDS.scholaops;
/**
 * The COMPANY, as opposed to a product brand: the name on the one Stripe account
 * and the one Meta app every tenant goes through (house-brand decision,
 * 2026-09-28 — docs/plans/dual-brand.md → "House brand").
 *
 * Not to be confused with `DEFAULT_BRAND`, which answers "we could not tell which
 * brand this is". This one answers "who is the legal counterparty a school meets
 * outside our CRM" — and a client of any OTHER brand has to be told that name
 * before it appears on their card statement or in Meta's pop-up.
 */
export const HOUSE_BRAND = BRANDS.general;
/**
 * What a charge reads on the card statement. Configured in the Stripe dashboard
 * (Settings → Business → Statement descriptor), not by the API — this constant is
 * the copy the CRM shows, and must be changed together with that setting.
 */
export const STATEMENT_DESCRIPTOR = "LOXAGO";
/** True for a value that is a key of the registry. */
export function isBrandKey(value) {
    return Object.prototype.hasOwnProperty.call(BRANDS, value);
}
/**
 * Narrows a `clients.brand` value onto a definition.
 *
 * Falls back rather than throwing: a brand lookup sits inside alert and email
 * paths, and a key this build does not recognise must degrade to the default
 * brand, never take down the notification it was decorating.
 */
export function brandByKey(key) {
    if (!key || !isBrandKey(key))
        return DEFAULT_BRAND;
    return BRANDS[key];
}
/**
 * Resolves the brand of an anonymous screen from `window.location.hostname`.
 *
 * Case-insensitive and port-tolerant, because `location.hostname` is lowercase
 * and portless but a caller may hand over `location.host` by mistake, and a
 * silent miss here renders the wrong brand rather than erroring. `localhost`,
 * preview deployments and anything unlisted get `DEFAULT_BRAND`.
 */
export function brandByHostname(hostname) {
    if (!hostname)
        return DEFAULT_BRAND;
    const host = hostname.trim().toLowerCase().split(":")[0];
    if (!host)
        return DEFAULT_BRAND;
    for (const brand of Object.values(BRANDS)) {
        if (brand.hostnames.includes(host))
            return brand;
    }
    return DEFAULT_BRAND;
}
/** The brands a super-admin may actually assign to a client. */
export function assignableBrands() {
    return Object.values(BRANDS).filter((b) => b.assignable);
}
/** `https://app.scholaops.ai/aviso-de-privacidad` — built, never written twice. */
export function privacyNoticeUrl(brand) {
    return `${brand.appUrl.replace(/\/$/, "")}${brand.privacyNoticePath}`;
}
/** `https://app.scholaops.ai/terminos-y-condiciones` — the twin of `privacyNoticeUrl`. */
export function termsUrl(brand) {
    return `${brand.appUrl.replace(/\/$/, "")}${brand.termsPath}`;
}
/**
 * The `fromName` of an outgoing email: `ScholaOps | visitas`.
 *
 * The brand here follows the RECIPIENT, not the client — billing alerts go to us
 * and keep the default brand however the client is branded.
 */
export function senderName(brand, suffix) {
    return `${brand.name} | ${suffix}`;
}
//# sourceMappingURL=brand.js.map